import {
  Button,
  Checkbox,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFetchGmailQueries, useFetchSources } from 'hooks/use-sources'
import { Feature, GmailQuery, SearchScopeType, Source, SourceType } from 'api/model'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import FtuxTooltip from 'components/FtuxTooltip'
import { useNavigate } from 'react-router-dom'
import AddEmailQueryModal from './AddEmailQueryModal'
import { useFtuxTooltip } from 'hooks/use-ftux'
import EmptyStateSection from './EmptyStateSection'
import { useFetchCheckPermissions } from 'hooks/use-permission'
import SetupPermissionsModal from 'components/Permissions/SetupPermissionsModal'
import List, { ListRenderer } from 'components/List'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import BulkActionBar from 'components/BulkActionBar'
import BulkActionModal from 'components/BulkActionBar/BulkActionModal'
import { formatTimestamp } from 'utils'
import { useActiveOrganization } from 'hooks/use-organization'
import { sourcesDeleteGmailQueries, sourcesGetGmailQuery } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { toastAtom } from 'state'
import { useSetRecoilState } from 'recoil'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import { COLORS } from 'app-constants'

// TODO: move this to utils, we need this because the terminology is Private / Public instead of owner / global
export function convertScopeToVisibility(scope: SearchScopeType) {
  return scope === SearchScopeType.owner ? 'Private' : 'Public'
}

const EmailsList = ({
  isLoading,
  gmailQueries,
  setGmailQueries,
  sourceId,
  gmailSource,
}: {
  isLoading: boolean
  gmailQueries: GmailQuery[]
  setGmailQueries: React.Dispatch<React.SetStateAction<GmailQuery[]>>
  sourceId: string
  gmailSource?: Source
}) => {
  const [activeOrganization] = useActiveOrganization()
  const isMobile = useCheckMobileScreen()
  const setToast = useSetRecoilState(toastAtom)

  const [isDeleting, setIsDeleting] = useState<boolean>(false)

  const [isBulkActionModalOpen, setIsBulkActionModalOpen] = useState<boolean>(false)

  const [searchText, setSearchText] = useState<string>('')
  const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const filteredQueries = useMemo(() => {
    return gmailQueries.filter((n) => {
      return n.query.toLowerCase().includes(searchText.toLowerCase())
    })
  }, [gmailQueries, searchText])

  const [checkedQueryIndices, setCheckedQueryIndices] = useState<number[]>([])

  const onItemChecked = (index: number) => {
    setCheckedQueryIndices((checkedQueryIndices) => {
      if (checkedQueryIndices.includes(index)) {
        return checkedQueryIndices.filter((i) => i !== index)
      }

      return [...checkedQueryIndices, index]
    })
  }

  const getListRenderer = (items: GmailQuery[], isCheckboxEnabled: boolean) => {
    return {
      rows: items.map((gq, index) => {
        return {
          key: gq.id,
          columnWidth: (column) => {
            switch (column) {
              case 0:
                return '24px'
              case 1:
                return '48px'
              case 2:
                return '48px'
              case 3:
                return '100px'
              case 4:
                return '100px'
            }
          },
          columnEntry: (column) => {
            switch (column) {
              case 0:
                return (
                  <>
                    {isCheckboxEnabled && (
                      <Checkbox
                        disableRipple
                        color="primary"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                          onItemChecked(index)
                        }}
                        checked={checkedQueryIndices.includes(index)}
                        sx={{
                          p: 0,
                          ml: '12px',
                        }}
                      />
                    )}
                  </>
                )
              case 1:
                return <Typography variant="pS">{gq.query}</Typography>
              case 2:
                return <Typography variant="pS">{gq.count}</Typography>
              case 3:
                return (
                  <>
                    {gq.date_indexed ? (
                      <Typography variant="pS">{formatTimestamp(gq.date_indexed)}</Typography>
                    ) : (
                      <Typography
                        variant="pS"
                        sx={{
                          whiteSpace: 'nowrap',
                        }}>
                        In progress
                      </Typography>
                    )}
                  </>
                )
              case 4:
                return <Typography variant="pS">{convertScopeToVisibility(gq.search_scope)}</Typography>
              default:
                return null
            }
          },
        }
      }),
      columnCount: 5,
      columnName: (column) => {
        switch (column) {
          case 0:
            return ''
          case 1:
            return 'Name'
          case 2:
            return 'Number of emails'
          case 3:
            return 'Last indexed'
          case 4:
            return (
              <Stack alignItems="center" direction="row" spacing="4px">
                <Typography variant="h6">Visibility</Typography>

                <Tooltip
                  enterTouchDelay={0}
                  title={
                    'Emails marked as private will not be used to generate twin responses in interactions with other users.'
                  }>
                  <InfoOutlined
                    sx={{
                      height: '16px',
                      width: '16px',
                      color: COLORS.GRAY_3,
                    }}
                  />
                </Tooltip>
              </Stack>
            )
          default:
            return null
        }
      },
    } as ListRenderer
  }

  const onBulkDeleteClick = () => {
    setIsBulkActionModalOpen(true)
  }

  const onDismissBulkActionBar = () => {
    setCheckedQueryIndices([])
  }

  const onCloseBulkActionModal = () => {
    setIsBulkActionModalOpen(false)
  }

  const onConfirmBulkDeletion = async () => {
    if (!activeOrganization || !sourceId) {
      return
    }

    try {
      setIsDeleting(true)

      const queryIdsToDelete = checkedQueryIndices.map((index) => filteredQueries[index].id)
      await sourcesDeleteGmailQueries(activeOrganization, sourceId, queryIdsToDelete)
      setToast({
        severity: 'success',
        title: 'Successfully deleted queries',
      })
      // update recoil state
      const updatedIndexedQueries = gmailQueries.filter((iq) => !queryIdsToDelete.includes(iq.id))
      setGmailQueries(updatedIndexedQueries)
    } catch (error) {
      setToast({
        severity: 'error',
        title: 'Failed to delete queries',
      })
    } finally {
      setIsDeleting(false)
      setIsBulkActionModalOpen(false)
    }
  }

  const { isFtuxTooltipOpen, setIsFtuxTooltipOpen } = useFtuxTooltip()
  const navigate = useNavigate()
  const { featurePermissions } = useFetchCheckPermissions(Feature.gmail)
  const hasPermissions = featurePermissions?.isPermitted ?? false
  const acceptedPermissions = featurePermissions?.acceptedPermissions ?? []
  const missingPermissions = featurePermissions?.missingPermissions ?? []
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState<boolean>(false)

  const [isAddEmailIndexModalOpen, setIsAddEmailIndexModalOpen] = useState(false)

  const intervalIdRef = useRef<number | null>(null)
  useEffect(() => {
    if (gmailQueries.length > 0) {
      const gmailQueryToFetch = gmailQueries.find((gq) => !gq.date_indexed)

      // start timeout if there is a gmail query that has not been indexed
      const id = window.setInterval(() => {
        fetchGmailQuery(gmailQueryToFetch?.id ?? '')
      }, 5000)
      intervalIdRef.current = id
    }

    // Cleanup function to clear the interval when the component unmounts
    return () => {
      if (intervalIdRef.current) {
        window.clearInterval(intervalIdRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onAddQueryClick = () => {
    // check to see if permissions are granted
    if (!hasPermissions) {
      setIsPermissionsModalOpen(true)
      return
    }

    // if so, set the modal to open
    setIsAddEmailIndexModalOpen(true)
  }

  const onClosePermissionsModal = () => {
    setIsPermissionsModalOpen(false)
  }

  const fetchGmailQuery = useCallback(
    async (gmailQueryId: string) => {
      if (!activeOrganization || !gmailSource || !gmailQueryId) {
        return
      }

      try {
        const response = await sourcesGetGmailQuery(activeOrganization, gmailSource?.id || '', gmailQueryId)
        // we only want to set the new gmail query if it has been indexed
        if (response.data.date_indexed) {
          setGmailQueries((gmailQueries) => {
            return gmailQueries.map((gq) => {
              if (gq.id === response.data.id) {
                return response.data
              }

              return gq
            })
          })

          // clear the interval upon successful fetch
          if (intervalIdRef.current) {
            window.clearInterval(intervalIdRef.current)
            intervalIdRef.current = null
          }

          setToast({ title: 'Successfully updated gmail query indexing status', severity: 'success' })
        }
      } catch (err) {
        setToast({ title: 'Failed to update gmail query indexing status', severity: 'error' })
      }
    },
    [activeOrganization, gmailSource, setGmailQueries, setToast]
  )

  const onEmailIndexAdded = (gmailQuery: GmailQuery) => {
    setGmailQueries((gmailQueries) => {
      return [gmailQuery, ...gmailQueries]
    })

    // when a new email index is added, we want to set the id to fetch to the new email index
    const id = window.setInterval(() => {
      fetchGmailQuery(gmailQuery.id)
    }, 5000)
    intervalIdRef.current = id
  }

  return (
    <Stack width="100%" maxWidth="1024px">
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <TextField
          placeholder="Search queries"
          value={searchText}
          onChange={onSearchTextChange}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{
                    marginRight: '-10px',
                    color: COLORS.GRAY_3,
                  }}
                />
              </InputAdornment>
            ),
          }}
          style={{
            width: isMobile ? '100%' : '260px',
          }}
        />

        <FtuxTooltip
          open={isFtuxTooltipOpen}
          setOpen={setIsFtuxTooltipOpen}
          headerText="Add queries to select emails"
          subtitleText="Use queries to select emails for your twin to reference when answering questions."
          progressText="3 of 6"
          onBackClick={() => {
            navigate('/my-twin/notes')
          }}
          onNextClick={() => {
            navigate('/my-twin/calendars')
          }}
          placement={isMobile ? 'bottom' : 'right-start'}
          onDismiss={() => {
            setIsFtuxTooltipOpen(false)
          }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={onAddQueryClick}
            startIcon={<AddOutlinedIcon />}
            sx={{
              maxHeight: '36px',
              width: '164px',
            }}>
            Add query
          </Button>
        </FtuxTooltip>
      </Stack>

      {filteredQueries.length === 0 ? (
        <EmptyStateSection headerText="No queries yet" />
      ) : (
        <Stack spacing="14px">
          <BulkActionBar
            isOpen={checkedQueryIndices.length > 0}
            buttons={[
              <Button
                size="medium"
                variant="outlined"
                onClick={onBulkDeleteClick}
                sx={{
                  color: COLORS.GRAY_0,
                  background: COLORS.PURE_WHITE,
                  border: `1px solid ${COLORS.GRAY_5}`,
                  '&:hover': {
                    borderWidth: '1px',
                  },
                }}>
                Delete
              </Button>,
            ]}
            onDismiss={onDismissBulkActionBar}
          />
          <List renderer={getListRenderer(filteredQueries, true)} isLoading={isLoading} />
          <BulkActionModal
            content={
              <List
                renderer={getListRenderer(
                  checkedQueryIndices.map((index) => filteredQueries[index]),
                  false
                )}
                isLoading={isLoading}
              />
            }
            onSubmitClick={onConfirmBulkDeletion}
            headerText="Are you sure you want to delete the following email queries?"
            isOpen={isBulkActionModalOpen}
            onClose={onCloseBulkActionModal}
            submitButtonText="Delete"
            isSubmitting={isDeleting}
          />
        </Stack>
      )}

      {isAddEmailIndexModalOpen && (
        <AddEmailQueryModal
          gmailQueries={gmailQueries}
          onEmailIndexAdded={onEmailIndexAdded}
          sourceId={gmailSource?.id ?? ''}
          onClose={() => setIsAddEmailIndexModalOpen(false)}
        />
      )}
      {isPermissionsModalOpen && (
        <SetupPermissionsModal
          onClose={onClosePermissionsModal}
          acceptedPermissions={acceptedPermissions}
          missingPermissions={missingPermissions}
          feature={Feature.gmail}
        />
      )}
    </Stack>
  )
}

const MyTwinEmails = () => {
  const isMobile = useCheckMobileScreen()

  const { isLoading: isLoadingSources, sources } = useFetchSources()
  const gmailSource = sources.find((s) => s.type === SourceType.gmail)

  const {
    gmailQueries,
    isLoading: isLoadingGmailQueries,
    setGmailQueries,
  } = useFetchGmailQueries(gmailSource?.id || '')

  if (!gmailSource) {
    return (
      <Stack width="100%" maxWidth="768px">
        {!isMobile && (
          <Typography variant="h4" mb={2}>
            Emails
          </Typography>
        )}

        <Typography>No email has been connected yet.</Typography>
      </Stack>
    )
  }

  const renderContent = () => {
    if (isLoadingSources || isLoadingGmailQueries) {
      return <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
    }

    return (
      <EmailsList
        isLoading={isLoadingSources || isLoadingGmailQueries}
        sourceId={gmailSource?.id ?? ''}
        gmailQueries={gmailQueries}
        setGmailQueries={setGmailQueries}
        gmailSource={gmailSource}
      />
    )
  }

  return (
    <Stack
      borderLeft="1px solid #DDDDDD"
      width="100%"
      height="100vh"
      p={3}
      sx={{
        background: COLORS.GRAY_8,
      }}>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems="center"
        justifyContent="space-between"
        maxWidth="1024px">
        <Stack mb={2}>
          {!isMobile && (
            <Typography variant="h4" mb={2}>
              Emails
            </Typography>
          )}

          <Typography
            width={isMobile ? '100%' : '620px'}
            variant="pS"
            sx={{
              color: COLORS.GRAY_2,
            }}>
            Add queries to index emails into your twin’s knowledge base.
          </Typography>
        </Stack>
      </Stack>

      {renderContent()}
    </Stack>
  )
}

export default MyTwinEmails
